import { CssBaseline } from '@mui/material';
import { ErrorBoundary } from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import log from 'loglevel';
import { SnackbarProvider } from 'notistack';
import { ReactNode, StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { RecoilRoot } from 'recoil';
import BrowserSupportWarning from './components/BrowserSupportWarning';
import InterpolatedSlicesDialog from './components/Dialogs/InterpolatedSlicesDialog';
import PatientConflictPrompt from './components/Dialogs/PatientConflictPrompt';
import JobQueue from './components/JobQueue';
import WarningSnackbarContent from './components/WarningSnackbarContent';
import { UnexpectedErrorPage } from './containers/errors/RouteErrorBoundary';
import UnsupportedEnvironmentPage from './containers/errors/UnsupportedEnvironmentPage';
import { AppServicesAndSessionBridge } from './contexts/AppServicesAndSessionBridge';
import BrokerAuthSessionProvider from './contexts/auth/BrokerAuthSessionProvider';
import MsalAuthSessionProvider from './contexts/auth/MsalAuthSessionProvider';
import { IntlProvider } from './i18n';
import {
    AuthConfigurationError,
    isAuthenticationEnabled,
    isEntraAuthEnabled,
    isBrokerAuthEnabled,
} from './lib/auth';
import { createMsalInstance } from './lib/msal';
import {
    clearStaleLoginState,
    completeLoginRedirect,
    getStoredUser,
    isLoginRedirectCallback,
} from './lib/oidc';
import router from './routes/router';
import { initializeSentry } from './sentry';
import './theme/fonts.css';
import './theme/index.css';
import ThemeProvider from './theme/ThemeProvider';
import AppConfig from './utils/appconfig';
import { getBrowserSupport } from './utils/browserSupport';
import { installConsoleBuffer } from './utils/console-buffer';
import { initializeItkWasmPipelines } from './lib/elastix';

// First, so the error page can show console output from the whole session.
installConsoleBuffer();

// dcmjs's "validation.dcmjs" logger emits .error() for every unknown VR it
// encounters during DICOM parsing (e.g. "Invalid vr type xs - using US").
// These are not actionable — dcmjs falls back to a sensible default and
// continues. Silencing only this namespace prevents console flooding and
// keeps these non-actionable warnings out of Sentry's console-capture
// integration while leaving the rest of dcmjs's logging intact.
log.getLogger('validation.dcmjs').setLevel(log.levels.SILENT);

declare module 'notistack' {
    interface VariantOverrides {
        jobQueue: true;
    }
}

const queryClient = new QueryClient();
const root = createRoot(document.getElementById('root')!);

// eslint-disable-next-line no-console
console.info(`Workspace+ version ${AppConfig.version}`);

// Initialize ITK-Wasm pipelines for image registration
initializeItkWasmPipelines().catch(console.error);

const AppProvidersAndRouter = () => (
    <IntlProvider>
        <ThemeProvider>
            <CssBaseline />
            <QueryClientProvider client={queryClient}>
                <SnackbarProvider
                    classes={{
                        containerAnchorOriginBottomRight:
                            'snackbar-container-bottom-right-default',
                    }}
                    maxSnack={3}
                    preventDuplicate
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    Components={{
                        jobQueue: JobQueue,
                        warning: WarningSnackbarContent,
                    }}
                >
                    <ErrorBoundary
                        fallback={({ error, componentStack, eventId }) => (
                            <UnexpectedErrorPage
                                error={error}
                                boundary="app"
                                componentStack={componentStack}
                                eventId={eventId}
                            />
                        )}
                    >
                        <AppServicesAndSessionBridge>
                            <RouterProvider router={router} />
                        </AppServicesAndSessionBridge>
                        <InterpolatedSlicesDialog />
                        <PatientConflictPrompt />
                        <BrowserSupportWarning />
                    </ErrorBoundary>
                </SnackbarProvider>
            </QueryClientProvider>
        </ThemeProvider>
    </IntlProvider>
);

const AppContent = (
    <RecoilRoot>
        <AppProvidersAndRouter />
    </RecoilRoot>
);

/**
 * Theme, translations and state for a full-page notice rendered in place of
 * the application, such as a startup failure.
 */
const StandalonePage = ({ children }: { children: ReactNode }) => (
    <StrictMode>
        <RecoilRoot>
            <IntlProvider>
                <ThemeProvider>
                    <CssBaseline />
                    {children}
                </ThemeProvider>
            </IntlProvider>
        </RecoilRoot>
    </StrictMode>
);

/**
 * Renders the blocking browser-support notice and reports it, when this
 * browser cannot run the viewer.
 *
 * Runs ahead of authentication so an unsupported browser is told so before
 * the user is sent through a sign-in they cannot make use of. The report is
 * PHI-free, so it is safe to log for support.
 *
 * @returns Whether the application was blocked from starting.
 */
function renderBlockingSupportNotice(): boolean {
    const support = getBrowserSupport();
    if (!support.blocked) {
        return false;
    }

    console.error(`Unsupported browser environment\n${support.diagnostics}`);
    root.render(
        <StandalonePage>
            <UnsupportedEnvironmentPage report={support} />
        </StandalonePage>
    );
    return true;
}

async function initializeAndRenderApp() {
    try {
        await AppConfig.initialize();
    } catch (error) {
        console.error('Failed to load app configuration:', error);
    }

    initializeSentry();

    if (renderBlockingSupportNotice()) {
        return;
    }

    if (!isAuthenticationEnabled()) {
        console.debug('Authentication is disabled.');
        root.render(<StrictMode>{AppContent}</StrictMode>);
        return;
    }

    try {
        if (isBrokerAuthEnabled()) {
            // Redeeming the code before the first render keeps the route guard
            // from seeing a not-yet-loaded session and bouncing to /login.
            const initialUser = isLoginRedirectCallback()
                ? await completeLoginRedirect()
                : await getStoredUser();
            // Housekeeping: never allow a cleanup failure to block sign-in.
            void clearStaleLoginState().catch((error) =>
                console.debug('Could not clear stale login state:', error)
            );

            const brokerContent = (
                <BrokerAuthSessionProvider initialUser={initialUser}>
                    {AppContent}
                </BrokerAuthSessionProvider>
            );

            // Entra is still mounted when configured: it is the only issuer of
            // the Azure resource tokens the Blob storage path needs. Only that
            // path depends on it, so its failure leaves the application
            // running without it rather than failing sign-in.
            if (isEntraAuthEnabled()) {
                try {
                    const { MsalProvider } = await import('@azure/msal-react');
                    const msalInstance = await createMsalInstance();
                    root.render(
                        <StrictMode>
                            <MsalProvider instance={msalInstance}>
                                {brokerContent}
                            </MsalProvider>
                        </StrictMode>
                    );
                    return;
                } catch (error) {
                    console.error(
                        'Entra setup failed; continuing without Azure storage access:',
                        error
                    );
                }
            }

            root.render(<StrictMode>{brokerContent}</StrictMode>);
            return;
        }

        const { MsalProvider } = await import('@azure/msal-react');
        const msalInstance = await createMsalInstance();

        root.render(
            <StrictMode>
                <MsalProvider instance={msalInstance}>
                    <MsalAuthSessionProvider>
                        {AppContent}
                    </MsalAuthSessionProvider>
                </MsalProvider>
            </StrictMode>
        );
    } catch (error) {
        if (error instanceof AuthConfigurationError || isBrokerAuthEnabled()) {
            // Fail closed: authentication is explicitly enabled (or its
            // configuration is invalid), so every bootstrap failure —
            // config, discovery, MSAL init — renders a setup error instead
            // of booting the app unauthenticated. The unauthenticated
            // fallback below is deliberately kept for the legacy Entra path.
            console.error('Authentication setup failed:', error);
            root.render(
                <StandalonePage>
                    <UnexpectedErrorPage error={error} boundary="auth-setup" />
                </StandalonePage>
            );
            return;
        }
        console.error('MSAL setup or initialization failed:', error);
        console.warn(
            'Proceeding without MSAL authentication due to an error during its setup.'
        );
        root.render(<StrictMode>{AppContent}</StrictMode>);
    }
}

// A bootstrap failure must never strand the user on a blank page: whatever
// threw, the generic error page still names the failure and links support.
initializeAndRenderApp().catch((error: unknown) => {
    console.error('Application startup failed:', error);
    root.render(
        <StandalonePage>
            <UnexpectedErrorPage error={error} boundary="bootstrap" />
        </StandalonePage>
    );
});
